"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useCallback,
  useState,
} from "react";
import type { Editor } from "@tiptap/core";

// Define el estado para cada editor
interface EditorStatus {
  hasChanges: boolean;
  isGenerating: boolean;
}

// El estado global será un objeto en el que cada clave (por ejemplo, el ID del editor)
// tiene un objeto con los dos estados.
type EditorStatuses = Record<string, EditorStatus>;

// Definición de acciones para el reducer
type Action =
  | { type: "UPDATE_STATUS"; payload: { id: string; status: Partial<EditorStatus> } }
  | { type: "RESET_STATUS"; payload: { id: string } }
  | { type: "RESET_ALL" };

// Reducer para actualizar el estado de forma inmutable
function statusReducer(state: EditorStatuses, action: Action): EditorStatuses {
  switch (action.type) {
    case "UPDATE_STATUS": {
      const prevStatus = state[action.payload.id] || { hasChanges: false, isGenerating: false };
      return {
        ...state,
        [action.payload.id]: {
          ...prevStatus,
          ...action.payload.status,
        },
      };
    }
    case "RESET_STATUS":
      return {
        ...state,
        [action.payload.id]: { hasChanges: false, isGenerating: false },
      };
    case "RESET_ALL":
      return {};
    default:
      return state;
  }
}

// Extiende el contexto para incluir además del editor, el estado granular de cada editor
interface EditorContextType {
  editor: Editor | null;
  setEditor: (editor: Editor | null) => void;
  // Estado granular de cada editor
  editorStatuses: EditorStatuses;
  updateEditorStatus: (id: string, status: Partial<EditorStatus>) => void;
  // Estados globales derivados: se activan si al menos un editor cumple la condición
  globalHasChanges: boolean;
  globalIsGenerating: boolean;
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: (value: boolean) => void;
}

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export const EditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  // useReducer para manejar el estado granular de cada editor
  const [editorStatuses, dispatch] = useReducer(statusReducer, {});

  // Función para actualizar el estado de un editor específico
  const updateEditorStatus = useCallback(
    (id: string, status: Partial<EditorStatus>) => {
      dispatch({ type: "UPDATE_STATUS", payload: { id, status } });
    },
    []
  );

  // Derivar un estado global para detectar si algún editor tiene cambios
  const globalHasChanges = useMemo(
    () => Object.values(editorStatuses).some((status) => status.hasChanges),
    [editorStatuses]
  );

  // Derivar un estado global para detectar si algún editor está generando contenido
  const globalIsGenerating = useMemo(
    () => Object.values(editorStatuses).some((status) => status.isGenerating),
    [editorStatuses]
  );

  // Memoizamos el valor del contexto para evitar renders innecesarios
  const contextValue = useMemo(
    () => ({
      editor,
      setEditor,
      editorStatuses,
      updateEditorStatus,
      globalHasChanges,
      globalIsGenerating,
      autoSaveEnabled,
      setAutoSaveEnabled,
    }),
    [editor, editorStatuses, updateEditorStatus, globalHasChanges, globalIsGenerating, autoSaveEnabled]
  );

  return <EditorContext.Provider value={contextValue}>{children}</EditorContext.Provider>;
};

export const useEditorContext = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error("useEditorContext debe usarse dentro de EditorProvider");
  }
  return context;
};
